import BaseApiService from "@/core/services/apiServices/BaseApiService";
import JwtService from "@/core/services/JwtService";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { Actions, Mutations } from "../enums/StoreEnums";
import RobotTypeValue from "@/models/enums/RobotType";
import { getWord } from "@/core/helpers/translator";
import { toLocalDate } from "@/core/helpers/dateHelper";
import RobotStatusValue from "@/models/enums/RobotStatus";
import MachineTypeValue from "@/models/enums/MachineType";
import AssetTypeValue from "@/models/enums/AssetType";
import TriggerTypeValue from "@/models/enums/TriggerType";
import ProcessTypeValue from "@/models/enums/ProcessType";
import PackageTypeValue from "@/models/enums/PackageType";

@Module
export default class SearchModule extends VuexModule {
    searchTerm = "";

    processes: any = {};
    packages: any = {};
    triggers: any = {};
    queues: any = {};
    assets: any = {};
    credentials: any = {};
    users: any = {};
    robots: any = {};
    machines: any = {};

    get getSearchTerm() { return this.searchTerm; }

    get getFoundProcesses() { return this.processes; }
    get getFoundPackages() { return this.packages; }
    get getFoundTriggers() { return this.triggers; }
    get getFoundQueues() { return this.queues; }
    get getFoundAssets() { return this.assets; }
    get getFoundCredentials() { return this.credentials; }
    get getFoundUsers() { return this.users; }
    get getFoundRobots() { return this.robots; }
    get getFoundMachines() { return this.machines; }

    @Mutation
    [Mutations.SET_SEARCH_TERM](data: string) {
        this.searchTerm = data;
    }

    @Mutation
    [Mutations.SET_PROCESS_SEARCH_RESULT](data: any) {
        if (data) {
            const list = data.data.map(function (value) {
                value.type = getWord(ProcessTypeValue.GetText(value.type));
                return value;
            });

            data.data = list;
            this.processes = data
        }
        else {
            this.processes = {
                hasData: true,
                data: [],
                dataType: "Process",
                hasFailed: true,
                messages: [{ isError: true, description: "NoItemsFound" }]
            }
        }
    }

    @Mutation
    [Mutations.SET_PACKAGE_SEARCH_RESULT](data: any) {
        if (data) {
            const list = data.data.map(function (_package) {
                return {
                    id: _package.id,
                    companyId: _package.companyId,
                    name: _package.name,
                    type: getWord(PackageTypeValue.GetText(_package.type)),
                    published: toLocalDate(_package.published),
                    latestVersion: _package.latestVersion,
                };
            });

            data.data = list;

            this.packages = data
        }
        else {
            this.packages = {
                hasData: true,
                data: [],
                dataType: "Package",
                hasFailed: true,
                messages: [{ isError: true, description: "NoItemsFound" }]
            }
        }
    }

    @Mutation
    [Mutations.SET_TRIGGER_SEARCH_RESULT](data: any) {
        if (data) {
            const wordYes = getWord("Yes");
            const wordNo = getWord("No");

            const list = data.data.map(function (trigger) {
                return {
                    id: trigger.id,
                    name: trigger.name,
                    companyId: trigger.companyId,
                    type: getWord(TriggerTypeValue.GetText(trigger.type)),
                    isDisabled: trigger.isDisabled,
                    isDisabledText: trigger.isDisabled ? wordYes : wordNo,
                    disableAt: toLocalDate(trigger.disableAt),
                };
            });

            data.data = list;
            this.triggers = data
        }
        else {
            this.triggers = {
                hasData: true,
                data: [],
                dataType: "Trigger",
                hasFailed: true,
                messages: [{ isError: true, description: "NoItemsFound" }]
            }
        }
    }

    @Mutation
    [Mutations.SET_QUEUE_SEARCH_RESULT](data: any) {
        if (data) {
            const secondsText = getWord("second(s)");
            const minutesText = getWord("minute(s)");
            const hoursText = getWord("hour(s)");

            const getAvarageTime = function (averageTimeInSeconds: number | undefined) {
                if (averageTimeInSeconds) {
                    if (averageTimeInSeconds < 60) {
                        return `${averageTimeInSeconds} ${secondsText}`;
                    }

                    let averageMinutes = averageTimeInSeconds / 60;
                    if (averageMinutes < 60) {
                        const avarageSeconds = averageTimeInSeconds % 60;
                        return `${parseInt(averageMinutes.toString())} ${minutesText} ${avarageSeconds} ${secondsText}`;
                    }

                    const averageHours = averageMinutes / 60;
                    averageMinutes = averageMinutes % 60;
                    return `${parseInt(averageHours.toString())} ${hoursText} ${averageMinutes} ${minutesText}`;
                }
                return "-";
            };

            const list = data.data.map((queue) => {
                queue.averageTime = getAvarageTime(queue.averageTimeInSeconds);
                return queue;
            })
            data.data = list;
            this.queues = data
        }
        else {
            this.queues = {
                hasData: true,
                data: [],
                dataType: "Queue",
                hasFailed: true,
                messages: [{ isError: true, description: "NoItemsFound" }]
            }
        }
    }


    @Mutation
    [Mutations.SET_ASSET_SEARCH_RESULT](data: any) {
        if (data) {
            Array.prototype.forEach.call(data.data, (value) => {
                value.type = AssetTypeValue.GetText(value.type);
            });
            this.assets = data
        }
        else {
            this.assets = {
                hasData: true,
                data: [],
                dataType: "Asset",
                hasFailed: true,
                messages: [{ isError: true, description: "NoItemsFound" }]
            }
        }
    }

    @Mutation
    [Mutations.SET_CREDENTIAL_SEARCH_RESULT](data: any) {
        if (data) {
            this.credentials = data;
        }
        else {
            this.credentials = {
                hasData: true,
                data: [],
                dataType: "Credential",
                hasFailed: true,
                messages: [{ isError: true, description: "NoItemsFound" }]
            }
        }
    }

    @Mutation
    [Mutations.SET_USER_SEARCH_RESULT](data: any) {
        if (data) {
            const wordYes = getWord("Yes");
            const wordNo = getWord("No");

            const list = data.data.map(function (user) {
                return {
                    id: user.id,
                    companyId: user.companyId,
                    username: user.username,
                    fullname: `${user.firstname} ${user.lastname}`,
                    email: user.email,
                    robotId: user.robotId,
                    lastLogin: toLocalDate(user.lastLogin),
                    defaultLanguageCode: user.defaultLanguageCode,
                    isLDAPUser: user.isLDAPUser ? wordYes : wordNo,
                    isRobotUser: user.robotId ? wordYes : wordNo,
                };
            });

            data.data = list;
            this.users = data;
        }
        else {
            this.users = {
                hasData: true,
                data: [],
                dataType: "User",
                hasFailed: true,
                messages: [{ isError: true, description: "NoItemsFound" }]
            }
        }
    }

    @Mutation
    [Mutations.SET_ROBOT_SEARCH_RESULT](data: any) {
        if (data) {
            const wordYes = getWord("Yes");
            const wordNo = getWord("No");

            const list = data.data.map(function (robot) {
                return {
                    id: robot.id,
                    companyId: robot.companyId,
                    name: robot.name,
                    type: getWord(RobotTypeValue.GetText(robot.type)),
                    status: getWord(RobotStatusValue.GetText(robot.status)),
                    lastStatusDate: toLocalDate(robot.lastStatusDate),
                    active: robot.active ? wordYes : wordNo,
                };
            });

            data.data = list;
            this.robots = data;
        }
        else {
            this.robots = {
                hasData: true,
                data: [],
                dataType: "Robot",
                hasFailed: true,
                messages: [{ isError: true, description: "NoItemsFound" }]
            }
        }
    }

    @Mutation
    [Mutations.SET_MACHINE_SEARCH_RESULT](data: any) {
        if (data) {
            const wordYes = getWord("Yes");
            const wordNo = getWord("No");

            const list = data.data.map(function (machine) {
                return {
                    id: machine.id,
                    companyId: machine.companyId,
                    name: machine.name,
                    type: MachineTypeValue.GetText(machine.machineType),
                    machineKey: machine.id,
                    isInUse: machine.isInUse ? wordYes : wordNo
                };
            });

            data.data = list;
            this.machines = data;
        }
        else {
            this.machines = {
                hasData: true,
                data: [],
                dataType: "Machine",
                hasFailed: true,
                messages: [{ isError: true, description: "NoItemsFound" }]
            }
        }
    }

    @Action
    [Actions.SET_SEARCH_RESULT_ORCH](data) {
        if (data) {
            this.context.commit(Mutations.SET_ROBOT_SEARCH_RESULT, data.robots);
        }
    }

    @Action
    [Actions.SET_SEARCH_RESULT_IDENTITY](data) {
        if (data) {
            this.context.commit(Mutations.SET_USER_SEARCH_RESULT, data.users);
        }
    }

    @Action
    [Actions.SET_SEARCH_RESULT_AGENT](data) {
        if (data) {
            this.context.commit(Mutations.SET_ASSET_SEARCH_RESULT, data.assets);
            this.context.commit(Mutations.SET_CREDENTIAL_SEARCH_RESULT, data.credentials);
            this.context.commit(Mutations.SET_PACKAGE_SEARCH_RESULT, data.packages);
            this.context.commit(Mutations.SET_PROCESS_SEARCH_RESULT, data.processes);
            this.context.commit(Mutations.SET_QUEUE_SEARCH_RESULT, data.queues);
            this.context.commit(Mutations.SET_TRIGGER_SEARCH_RESULT, data.triggers);
        }
    }

    @Action
    [Actions.SET_SEARCH_RESULT_CUSTOMER](data) {
        if (data) {
            this.context.commit(Mutations.SET_MACHINE_SEARCH_RESULT, data.data.machines);
        }
    }
}